import {
  BackButton,
  Button,
  Heading,
  Input,
  Line,
  Tooltip,
} from '@mm/components';
import PrimarySelect, {
  DropdownOption,
} from '@mm/components/legacy-components/PrimarySelect/PrimarySelect';
import {
  SlackAccount,
  SlackChannels,
  SlackFormFields,
} from '@mm/state/model/SlackModel';
import { selectSlackAccounts } from '@mm/state/selectors/slackSelectors';
import { useSlackCRUDActions } from '@mm/state/actions/SlackCRUDActions';
import { selectAudienceSorted } from '@mm/state/selectors/messengerAdSelectors';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import css from './SlackNotificationEditor.module.css';
import { CustomersAiLogo, SlackIcon } from '@mm/icons';
import {
  CheckCircleFilled,
  CloseOutlined,
  SlackOutlined,
  SyncOutlined,
  WarningFilled,
} from '@ant-design/icons';
import { StoreState } from '@mm/state/store-interfaces';
import { useConnectSlack } from '../../../../../features/beta/modules/Integrations/NativeIntegrations/components/SlackIntegrations/use-connect-slack';
import { SlideoutMenu as Menu } from '../../../components/SlideoutMenu';
import ImageUploader from '../../../../../../common/components/ImageUploader/ImageUploader';
import { Editor } from '../../SlideoutEmail/editor/components/Editor';

import {
  getHTMLStringFromState,
  getHTMLText,
} from '../../SlideoutEmail/editor/utils';
import classNames from 'classnames';

interface Props {
  formik: any;
  setField: (field: string, value: any) => void;
  getField: (field: string) => any;
  onSave: (isTest: boolean) => void;
  test: () => void;
  close: () => void;
  busy: boolean;
  setRef: (ref: any) => void;
  setIsTestSuccess: (value?: boolean) => void;
  isTestSuccess?: boolean;
  showBackButton?: boolean;
  withAudienceSelect?: boolean;
}

export const SlackNotificationEditor: React.FunctionComponent<
  Props
> = props => {
  const {
    formik,
    setField,
    getField,
    onSave,
    test,
    close,
    busy,
    setRef,
    isTestSuccess,
    setIsTestSuccess,
    withAudienceSelect = false,
    showBackButton = false,
  } = props;
  const slackBusy = useSelector((state: StoreState) => state.slack.busy);
  const attributes = useSelector(
    (state: StoreState) => state.attribute.entities,
  );
  const accounts = useSelector(selectSlackAccounts);

  const { connectSlack } = useConnectSlack();
  const { getChannels } = useSlackCRUDActions();
  const audiences = useSelector(selectAudienceSorted);
  const activeFbPageId = useSelector(activeFacebookPageIdSelector);

  const [channelsList, setChannelsList] = useState<SlackChannels[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<SlackAccount>(
    accounts.find(
      acc =>
        acc.teamId === getField('teamId') || acc.id === getField('accountId'),
    ) || accounts[0],
  );

  const sendAsBotOptions = [
    { id: 1, name: 'Yes' },
    { id: 2, name: 'No' },
  ];

  const sendAsBot = sendAsBotOptions[0];
  const isSendAsBot = getField('sendAsBot');
  const accountsOptions = accounts.map(account => ({
    ...account,
    name: account.teamName,
  }));

  const retrieveChannels = async (teamId, force = false) => {
    const accountChannelsList = await getChannels(
      teamId,
      activeFbPageId,
      force,
    );

    setChannelsList(accountChannelsList);
  };

  const onWorkspaseChange = (account: SlackAccount) => {
    retrieveChannels(account.teamId);
    setField('teamId', account.teamId);
    setField('teamName', account.teamName);
    setField('slackChannelName', '');
    setField('slackChannelId', '');
    setField('accountId', account.id);
    setSelectedAccount(account);
  };

  const onSendAsBotChange = (option: DropdownOption) => {
    setField('sendAsBot', option.id === sendAsBot.id ? true : false);
    if (option.id !== sendAsBot.id) {
      setField('botName', null);
      setField('assetUrl', null);
    } else if (!getField('botName')) {
      setField('botName', 'Customers.ai');
    }
  };

  useEffect(() => {
    formik.validateForm();

    if (selectedAccount) {
      retrieveChannels(selectedAccount.teamId);
    }
  }, []);

  const isSaveDisabled =
    busy ||
    slackBusy ||
    !getField('teamId') ||
    !getField('slackChannelId') ||
    !getHTMLText(getField('content'));
  const { teamId, slackChannelId, content } =
    formik.errors as unknown as SlackFormFields;

  return (
    <Menu className={css.container} setRef={setRef}>
      <div className={css.header}>
        {showBackButton && <BackButton onClick={close} />}
        <Heading size={3}>Slack Notifications</Heading>
        <Tooltip
          title={
            isSaveDisabled ? 'Workspace, channel and message are required.' : ''
          }
        >
          <div className={css.buttonsRow}>
            <Button
              css={{ container: css.save }}
              onClick={test}
              disabled={isSaveDisabled}
            >
              Test Connection
            </Button>

            <Button
              css={{ container: css.save }}
              onClick={() => onSave(false)}
              disabled={isSaveDisabled}
            >
              Save
            </Button>
          </div>
        </Tooltip>
        <span className={css.close} onClick={close}>
          <CloseOutlined />
        </span>
      </div>
      {isTestSuccess !== undefined && (
        <div
          className={classNames(css.row, css.testBlock, {
            [css.failedTest]: !isTestSuccess,
          })}
        >
          <span
            className={css.closeTestBlock}
            onClick={() => setIsTestSuccess(undefined)}
          >
            <CloseOutlined />
          </span>
          <div className={css.column}>
            {isTestSuccess ? (
              <span className={css.testNoteBold}>
                {' '}
                <CheckCircleFilled className={css.checkCircule} /> Message sent
                successfully!{' '}
              </span>
            ) : (
              <span className={css.testNoteBold}>
                <WarningFilled className={css.warningIcon} />
                Test failed
              </span>
            )}
            <span className={css.testNote}>
              {' '}
              {isTestSuccess
                ? 'Check your Slack channel to view it'
                : 'Check your notification settings'}
            </span>
          </div>
        </div>
      )}
      <Menu.Content className={css.content}>
        {withAudienceSelect && (
          <>
            <div className={css.row}>
              <div className={css.subtitle}>
                {' '}
                <SlackIcon /> Customers.ai Audience{' '}
              </div>
              <div className={css.column}>
                <PrimarySelect
                  className={css.select}
                  activeItemClassName={css.activeItem}
                  placeholder="Choose Audience"
                  options={audiences}
                  value={audiences.find(
                    audience => audience.id === getField('audienceId'),
                  )}
                  onChange={audience => setField('audienceId', audience.id)}
                />
                <div className={css.action} onClick={connectSlack}>
                  <SlackOutlined color="#2f54eb" /> Connect Workspace
                  {!selectedAccount.teamId && (
                    <div className={css.error}>{teamId}</div>
                  )}
                </div>
              </div>
            </div>
            <Line className={css.line} />
          </>
        )}
        <div className={css.row}>
          <div className={css.subtitle}>
            {' '}
            <SlackIcon /> Workspace{' '}
          </div>
          <div className={css.column}>
            <PrimarySelect
              className={css.select}
              activeItemClassName={css.activeItem}
              placeholder="Choose Workspace"
              options={accountsOptions}
              value={selectedAccount}
              displayNameKey="teamName"
              onChange={account => onWorkspaseChange(account as SlackAccount)}
            />
            <div className={css.action} onClick={connectSlack}>
              <SlackOutlined color="#2f54eb" /> Connect Workspace
              {!selectedAccount.teamId && (
                <div className={css.error}>{teamId}</div>
              )}
            </div>
          </div>
        </div>
        <Line className={css.line} />
        <div className={css.row}>
          <div className={css.subtitle}>
            {' '}
            <SlackIcon /> Channel{' '}
          </div>
          <div className={css.column}>
            <PrimarySelect
              className={css.select}
              activeItemClassName={css.activeItem}
              placeholder="Choose a Channel"
              options={channelsList}
              value={channelsList.find(
                channel => channel.name === getField('slackChannelName'),
              )}
              onChange={channel => {
                setField('slackChannelName', channel.name);
                setField('slackChannelId', channel.id);
              }}
              isLoading={slackBusy}
            />
            <div
              className={css.action}
              onClick={() => retrieveChannels(selectedAccount.teamId, true)}
            >
              <SyncOutlined color="#2f54eb" /> Refresh list
              {!getField('slackChannelId') && (
                <div className={css.error}>{slackChannelId}</div>
              )}
            </div>
          </div>
        </div>
        <Line className={css.line} />
        <div className={css.column}>
          <div className={css.subtitle}>
            <SlackIcon />
            Message Text
          </div>

          <Editor
            styleMenubar={{ display: 'none' }}
            attributes={attributes}
            value={getField('content')}
            onChange={state => {
              setField('content', getHTMLStringFromState(state));
            }}
          />
          <div>
            {!getHTMLText(getField('content')) && (
              <div className={css.error}>{content || 'Message Required'}</div>
            )}
          </div>
        </div>
        <Line className={css.line} />
        <div className={css.row}>
          <div className={css.subtitle}>
            {' '}
            <SlackIcon /> Send as a bot?{' '}
          </div>
          <div className={css.column}>
            <PrimarySelect
              className={css.select}
              activeItemClassName={css.activeItem}
              options={sendAsBotOptions}
              value={getField('sendAsBot') ? sendAsBot : sendAsBotOptions[1]}
              onChange={option => onSendAsBotChange(option)}
            />
            <div className={css.note}>
              If you choose no, this message will appear from you.
            </div>
          </div>
        </div>
        <Line className={css.line} />
        <div className={css.row}>
          <div className={css.subtitle}>
            <SlackIcon /> Image for the bot
          </div>
          <div className={css.column}>
            <ImageUploader
              className={css.imageUploader}
              css={{ dropArea: css.imageUploaderDropArea }}
              needsFileModification
              file={getField('assetUrl') || ''}
              onUpload={(_, url) => setField('assetUrl', url)}
              onRemove={() => setField('assetUrl', null)}
              disabled={!isSendAsBot}
              fixedRatio
              withRemoveBtn
              newStyle
            />
            <div className={css.note}>
              Image should be square and a minimum of 512x512 pixels, but no
              larger than 1024x1024 pixels.
            </div>
          </div>
        </div>
        <Line className={css.line} />
        <div className={css.row}>
          <div className={css.subtitle}>
            {' '}
            <SlackIcon /> Bot Name{' '}
          </div>
          <div className={css.column}>
            <Input
              css={{
                container: classNames(css.input, {
                  [css.inputDisabled]: !isSendAsBot,
                }),
              }}
              value={getField('botName') || 'Customers.ai'}
              disabled={!isSendAsBot}
              onChange={e => setField('botName', e.target.value)}
              placeholder="Customers.ai"
              type="text"
            />
            <div className={css.note}>
              Specify the bot name which appears as a bold username above the
              message inside Slack. Defaults to Customers.ai.
            </div>
          </div>
        </div>
      </Menu.Content>
    </Menu>
  );
};
